
import { User, ProfileUpdateData, CompanyInfo, UserProfile, HydraCollection } from '../types';
import { put, get, patch } from '@/services/apiService';

export const updateUserProfile = async (data: ProfileUpdateData, token: string, profileId: string): Promise<{ success: boolean; updatedUserProfile?: UserProfile; message?: string }> => {
  try {
    console.log('Updating profile with data:', data);
    const response = await patch<UserProfile>(profileId, data);

    // Ensure we have the complete updated user object with the right structure
    const updatedUserProfile: UserProfile = response.data;

    return {
      success: true,
      updatedUserProfile
    };
  } catch (error) {
    console.error('Profile update error:', error);
    return {
      success: false,
      message: "Kapcsolati hiba történt. Kérjük, próbálja újra később."
    };
  }
};

export const updateCompanyInfo = async (companyInfo: CompanyInfo, token: string, profileId: string): Promise<{ success: boolean; message?: string }> => {
  try {
    // We're updating the company info as part of the profile update
    // Make sure all required fields are included
    const data: ProfileUpdateData = {
      owner: 'me',
      name: { firstName: '', lastName: '' },  // Adding required name field
      email: '',  // Adding required email field
      companyInfo: companyInfo
    };

    const response = await patch<User>(profileId, data);

    return {
      success: true
    };
  } catch (error) {
    console.error('Company info update error:', error);
    return {
      success: false,
      message: "Kapcsolati hiba történt. Kérjük, próbálja újra később."
    };
  }
};

export const getUserProfile = async (token: string): Promise<{ success: boolean; user?: User; message?: string }> => {
  try {
    const response = await get<User>('/me');
    console.log('Raw API response:', response);

    if (!response.data) {
      throw new Error('No data received from API');
    }

    const responseData = response.data;
    console.log('Processing user data:', responseData);

    // Specifically log and handle companyInfo
    console.log('CompanyInfo from user profile:', responseData.userProfile?.companyInfo);

    // Create the user object from the response
    const user: User = {
      '@id': responseData['@id'],
      '@context': responseData['@context'],
      '@type': responseData['@type'],
      email: responseData.email,
      roles: responseData.roles || ['ROLE_MEDIHUB_USER'],
      emailVerified: true,
      userProfile: {
        '@id': responseData.userProfile?.['@id'],
        '@context': responseData.userProfile?.['@context'],
        '@type': responseData.userProfile?.['@type'],
        id: responseData.userProfile?.id,
        name: responseData.userProfile?.name || {
          firstName: '',
          lastName: '',
          academicTitle: '',
          formalSalutation: ''
        },
        owner: responseData.userProfile?.owner,
        notificationPreferences: responseData.userProfile?.notificationPreferences,
        companyInfo: responseData.userProfile?.companyInfo,
        website: responseData.userProfile?.website || '',
        contactPhone: responseData.userProfile?.contactPhone || '',
      }
    };

    if (responseData.userProfile?.name) {
      user.userProfile.name = {
        '@context': responseData.userProfile.name['@context'],
        '@id': responseData.userProfile.name['@id'],
        '@type': responseData.userProfile.name['@type'],
        firstName: responseData.userProfile.name.firstName || '',
        lastName: responseData.userProfile.name.lastName || '',
        academicTitle: responseData.userProfile.name.academicTitle || '',
        formalSalutation: responseData.userProfile.name.formalSalutation || ''
      };
    }

    console.log('Transformed user object with company info:', user);
    console.log('Company info in user profile after transformation:', user.userProfile.companyInfo);

    return {
      success: true,
      user
    };
  } catch (error) {
    console.error('Profile fetch error:', error);
    return {
      success: false,
      message: "Kapcsolati hiba történt. Kérjük, próbálja újra később."
    };
  }
};

export const getUserProfiles = async (): Promise<{ success: boolean; profiles?: UserProfile[]; message?: string }> => {
  try {
    const response = await get<HydraCollection<UserProfile>>('/user_profiles');

    if (!response.data || !response.data['hydra:member']) {
      throw new Error('Invalid response format');
    }

    return {
      success: true,
      profiles: response.data['hydra:member']
    };
  } catch (error) {
    console.error('Profiles fetch error:', error);
    return {
      success: false,
      message: "Kapcsolati hiba történt a profilok lekérésekor."
    };
  }
};
